import {HttpErrorResponse} from '@angular/common/http';
import {computed, effect, inject, Injectable, Injector, resource, WritableResource} from '@angular/core';
import {AuthenticationService} from '@authentication/authentication.service';
import {UserService as MiddlewareUserService, UserResponse} from '@middleware';
import {catchError, lastValueFrom, of} from 'rxjs';

@Injectable({providedIn: 'root'})
export class UserService {
  private readonly mwUserService = inject(MiddlewareUserService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly injector: Injector = Injector.create({providers: [], parent: inject(Injector)});

  public user: WritableResource<UserResponse | null> = resource({
    // Listen to changes in the authentication token
    // and reload the user when it changes.
    params: () => ({token: this.authenticationService.token()}),

    // This will run any time the authentication token changes. The user Resource value will be set
    // to the resolved value of this loader.
    loader: async args => {
      const token = args.params.token;

      if (!token) {
        return null;
      }

      return await lastValueFrom(
        this.mwUserService.getUser('me').pipe(
          catchError((err: HttpErrorResponse) => {
            console.error('Failed to load current user:', err);
            return of(null);
          }),
        ),
      );
    },
  });

  /**
   * This signal is useful for components that want to react to changes in the user, without having to
   * inspect the internal state of the Resource.
   *
   * This signal is a **read-only computed signal** that tracks the value of the
   * internal Resource's signal. It ensures external consumers cannot mutate the state
   * and can only read its current value.
   **/
  public readonly userSignal = computed(() => this.user.value());

  /** Returns a promise that resolves to the current user, waiting if necessary for the user to be loaded. */
  public get userPromise(): Promise<UserResponse | null> {
    return this._userPromise();
  }

  private async _userPromise(): Promise<UserResponse | null> {
    // If the user is logged in already, return the user immediately.
    if (this.user.hasValue()) return this.userSignal();

    // Otherwise, wait for the user to be loaded, but destroy the listener once we have the user.
    return this.waitForUser();
  }

  // Wait for the user Resource to load and return the value.
  async waitForUser(): Promise<UserResponse | null> {
    return new Promise((resolve, reject) => {
      const effectRef = effect(
        () => {
          if (this.user.hasValue()) {
            // User is finally loaded, resolve the promise and clean up the effect listener.
            resolve(this.userSignal());
            effectRef.destroy();
          }
        },
        {manualCleanup: true, injector: this.injector},
      );
    });
  }

  async reloadUser(): Promise<UserResponse> {
    this.user.reload();
    return await this.userPromise;
  }
}
